/** @file Default play seed + token/drawing types. */
import { shortId } from "./ids.js";

/**
 * @typedef {"player" | "goalkeeper" | "ball"} TokenKind
 * @typedef {"home" | "away" | null} TeamSide
 * @typedef {"TW" | "LA" | "RL" | "RM" | "RR" | "RA" | "KM" | null} PositionLabel
 */

/**
 * @typedef {object} Token
 * @property {string} id
 * @property {TokenKind} kind
 * @property {TeamSide} team
 * @property {number} number   // 0 = ball
 * @property {PositionLabel} label
 * @property {string | undefined} [color]  // optional team-specific fill override
 * @property {number} x        // meters, 0..40
 * @property {number} y        // meters, 0..20
 */

/**
 * @typedef {"run" | "pass" | "shot"} DrawingType
 * @typedef {object} Drawing
 * @property {string} id
 * @property {DrawingType} type
 * @property {string} color
 * @property {{x:number,y:number}[]} points   // at least 2, meter-space
 */

/**
 * @typedef {"select" | "run" | "pass" | "shot" | "erase"} ToolMode
 */

/**
 * @typedef {object} Scene
 * @property {string} id
 * @property {Record<string, {x:number, y:number}>} tokens   token-id → pos
 * @property {{x:number, y:number} | null} ball
 * @property {string[]} visibleArrowIds
 * @property {number} durationMs
 */

/**
 * Create a scene by snapshotting the current token/ball positions.
 * @param {Token[]} tokens
 * @param {string[]} [visibleArrowIds]
 * @param {number} [durationMs]
 * @returns {Scene}
 */
export function createInitialScene(tokens, visibleArrowIds = [], durationMs = 2000) {
  /** @type {Record<string, {x:number, y:number}>} */
  const positions = {};
  /** @type {{x:number, y:number} | null} */
  let ball = null;
  for (const t of tokens) {
    if (t.kind === "ball") {
      ball = { x: t.x, y: t.y };
      continue;
    }
    positions[t.id] = { x: t.x, y: t.y };
  }
  return {
    id: shortId(),
    tokens: positions,
    ball,
    visibleArrowIds: [...visibleArrowIds],
    durationMs,
  };
}

/**
 * Deep clone a scene (safe for history snapshots).
 * @param {Scene} s
 * @returns {Scene}
 */
export function cloneScene(s) {
  /** @type {Record<string, {x:number, y:number}>} */
  const tokens = {};
  for (const [id, p] of Object.entries(s.tokens)) {
    tokens[id] = { x: p.x, y: p.y };
  }
  return {
    id: s.id,
    tokens,
    ball: s.ball ? { x: s.ball.x, y: s.ball.y } : null,
    visibleArrowIds: [...s.visibleArrowIds],
    durationMs: s.durationMs,
  };
}

/**
 * Apply a scene's positions onto a token list (immutable). Keeps token metadata
 * (kind, team, number, label) but overwrites x/y from the scene.
 * Ball-kind token takes its position from scene.ball.
 * @param {Scene} scene
 * @param {Token[]} tokens
 * @returns {Token[]}
 */
export function projectSceneToTokens(scene, tokens) {
  return tokens.map((t) => {
    if (t.kind === "ball") {
      if (!scene.ball) {
        return t;
      }
      return { ...t, x: scene.ball.x, y: scene.ball.y };
    }
    const pos = scene.tokens[t.id];
    if (!pos) {
      return t;
    }
    return { ...t, x: pos.x, y: pos.y };
  });
}

/** Default team colors (fill). */
export const TEAM_COLORS = /** @type {const} */ ({
  home: "#1e40af",
  away: "#b91c1c",
});

/** Palette for drawings. */
export const DRAW_COLORS = /** @type {const} */ ([
  "#1f2937",
  "#2563eb",
  "#dc2626",
  "#16a34a",
  "#eab308",
  "#ea580c",
]);

/**
 * Build the default 7v7 + 2 GK + ball layout.
 * Home team on left half, away team on right half. Both in 6:0 defense formation
 * facing their goal — sensible visual starting point.
 * @returns {Token[]}
 */
export function createInitialTokens() {
  /** @type {Token[]} */
  const tokens = [];

  // Home goalkeeper
  tokens.push({
    id: shortId(),
    kind: "goalkeeper",
    team: "home",
    number: 1,
    label: "TW",
    x: 1.5,
    y: 10,
  });

  // Home defense 6:0 (6 field players) + Kreisläufer (7th) in front of own goal
  const homeXs = [8.5, 8.5, 8.5, 8.5, 8.5, 8.5, 11];
  const homeYs = [4, 7, 9, 11, 13, 16, 10];
  const homeLabels = /** @type {PositionLabel[]} */ (["LA", "RL", "RM", "RM", "RR", "RA", "KM"]);
  homeYs.forEach((y, i) => {
    tokens.push({
      id: shortId(),
      kind: "player",
      team: "home",
      number: i + 2,
      label: homeLabels[i] ?? null,
      x: homeXs[i] ?? 8.5,
      y,
    });
  });

  // Away goalkeeper
  tokens.push({
    id: shortId(),
    kind: "goalkeeper",
    team: "away",
    number: 1,
    label: "TW",
    x: 38.5,
    y: 10,
  });

  // Away offense in Grundaufstellung (6 Rückraum + 1 Kreisläufer = 7 field players)
  const awayXs = [18, 20, 20, 22, 22, 18, 29];
  const awayYs = [4, 7, 10, 13, 16, 10, 10];
  const awayLabels = /** @type {PositionLabel[]} */ (["LA", "RL", "RM", "RR", "RA", null, "KM"]);
  awayXs.forEach((x, i) => {
    tokens.push({
      id: shortId(),
      kind: "player",
      team: "away",
      number: i + 2,
      label: awayLabels[i] ?? null,
      x,
      y: awayYs[i] ?? 10,
    });
  });

  // Ball at center
  tokens.push({
    id: shortId(),
    kind: "ball",
    team: null,
    number: 0,
    label: null,
    x: 20,
    y: 10,
  });

  return tokens;
}

/**
 * @param {Token} token
 * @returns {string} hex color
 */
export function tokenFillColor(token) {
  if (token.kind === "ball") {
    return "#ffffff";
  }
  if (token.color) {
    return token.color;
  }
  if (token.team === "home") {
    return TEAM_COLORS.home;
  }
  if (token.team === "away") {
    return TEAM_COLORS.away;
  }
  return "#64748b";
}

/**
 * @param {Token} token
 * @returns {string} hex color
 */
export function tokenStrokeColor(token) {
  if (token.kind === "ball") {
    return "#111827";
  }
  if (token.kind === "goalkeeper") {
    return "#fbbf24"; // gold — distinguishes TW
  }
  return "#ffffff";
}
